// API Configuration
// Use environment variable if set, otherwise use relative URL for production or localhost for development
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (when served from same domain), use relative URL
  // In development, use localhost
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  return 'http://localhost:5000/api';
};

export const API_BASE_URL = getApiBaseUrl();

// Allowed MIME types - must match server-side whitelist
export const ALLOWED_MIME_TYPES = [
  // Video formats
  'video/mp4',
  'video/quicktime', // MOV
  'video/x-msvideo', // AVI
  'video/x-matroska', // MKV
  'video/webm',
  'video/x-flv',
  'video/x-ms-wmv',
  'video/mpeg',
  'video/3gpp',
  'video/x-m4v',
  // Raw video formats
  'video/x-redcode', // RED .R3D
  'video/x-arriraw', // ARRI RAW
  'video/x-sonyraw', // Sony RAW
  'video/x-canonraw', // Canon RAW video
  'video/x-panasonicraw', // Panasonic RAW
  'video/x-blackmagicraw', // Blackmagic RAW
  'video/x-dnxhd', // Avid DNxHD
  'video/x-prores', // Apple ProRes
  'video/x-cineform', // CineForm
  'application/x-redcode', // RED R3D (alternative MIME)
  'application/x-arriraw', // ARRI RAW (alternative MIME)
  // Audio formats
  'audio/mpeg', // MP3
  'audio/mp4',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/flac',
  'audio/aac',
  'audio/ogg',
  'audio/vorbis',
  'audio/x-ms-wma',
  'audio/webm',
  'audio/x-aiff',
  'audio/x-aif',
  // Image formats
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/webp',
  'image/svg+xml',
  'image/x-icon',
  // Raw image formats
  'image/x-canon-cr2', // Canon CR2
  'image/x-canon-crw', // Canon CRW
  'image/x-nikon-nef', // Nikon NEF
  'image/x-nikon-nrw', // Nikon NRW
  'image/x-sony-arw', // Sony ARW
  'image/x-sony-sr2', // Sony SR2
  'image/x-sony-srf', // Sony SRF
  'image/x-panasonic-rw2', // Panasonic RW2
  'image/x-olympus-orf', // Olympus ORF
  'image/x-fuji-raf', // Fuji RAF
  'image/x-pentax-pef', // Pentax PEF
  'image/x-adobe-dng', // Adobe DNG
  'image/x-kodak-dcr', // Kodak DCR
  'image/x-kodak-k25', // Kodak K25
  'image/x-kodak-kdc', // Kodak KDC
  'image/x-minolta-mrw', // Minolta MRW
  'image/x-sigma-x3f', // Sigma X3F
  'image/x-hasselblad-3fr', // Hasselblad 3FR
  'image/x-hasselblad-fff', // Hasselblad FFF
  'image/x-phaseone-iiq', // Phase One IIQ
  'image/x-leica-rwl', // Leica RWL
  'image/x-canon-raw', // Canon RAW (generic)
  'image/x-nikon-raw', // Nikon RAW (generic)
  'image/x-sony-raw', // Sony RAW (generic)
  'image/x-panasonic-raw', // Panasonic RAW (generic)
  'image/x-olympus-raw', // Olympus RAW (generic)
  'image/x-fuji-raw', // Fuji RAW (generic)
  'image/x-pentax-raw', // Pentax RAW (generic)
  'image/x-adobe-raw', // Adobe RAW (generic)
  'image/x-kodak-raw', // Kodak RAW (generic)
  'image/x-minolta-raw', // Minolta RAW (generic)
  'image/x-sigma-raw', // Sigma RAW (generic)
  'image/x-hasselblad-raw', // Hasselblad RAW (generic)
  'image/x-phaseone-raw', // Phase One RAW (generic)
  'image/x-leica-raw', // Leica RAW (generic)
  // Document formats
  'application/pdf',
  'application/msword', // DOC
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.ms-excel', // XLS
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.ms-powerpoint', // PPT
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'application/vnd.oasis.opendocument.text', // ODT
  'application/vnd.oasis.opendocument.spreadsheet', // ODS
  'application/vnd.oasis.opendocument.presentation', // ODP
  'application/rtf',
  'text/plain',
  'text/csv',
  'text/html',
  'text/xml',
  'application/xml',
  'application/json',
  // Archive formats
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/x-rar',
  'application/x-7z-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
  'application/x-bzip2',
  // Other common formats
  'application/octet-stream', // Generic binary (for unknown/raw formats)
  'application/x-binary', // Generic binary
  'application/x-unknown', // Unknown type fallback
];

// Upload configuration constants
export const CHUNK_SIZE_MIN = 1 * 1024 * 1024; // 1MB minimum
export const CHUNK_SIZE_MAX = 20 * 1024 * 1024; // 20MB maximum
export const CHUNK_SIZE_DEFAULT = 5 * 1024 * 1024; // 5MB default
export const CHUNK_SIZE_STEP = 2 * 1024 * 1024; // 2MB adjustment step
export const SPEED_THRESHOLD_FAST = 10 * 1024 * 1024; // 10 MB/s - increase chunk size
export const SPEED_THRESHOLD_SLOW = 2 * 1024 * 1024; // 2 MB/s - decrease chunk size
export const SPEED_SAMPLES_TO_CONSIDER = 3; // Number of recent chunks to average

// Timeout and retry configuration
export const CHUNK_UPLOAD_TIMEOUT_MS = 60000; // 60 seconds timeout for chunk upload (increased to handle slow uploads to Google Drive)
export const RETRY_TIMEOUT_MULTIPLIER = 4; // Retry if upload takes 4x longer than average
export const MAX_RETRY_ATTEMPTS = 3; // Maximum retry attempts per chunk
export const RETRY_DELAY_MS = 1000; // Initial delay before retry (exponential backoff)

